import { firebaseConfig } from '../../config/firebase';
import type {
  Announcement,
  AnnouncementAction,
  AnnouncementImageUpload,
  AnnouncementInput,
  AnnouncementStatus,
} from '../../types/announcement';
import { apiFetch } from './client';

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Could not read the selected image'));
    reader.readAsDataURL(file);
  });
}

export async function getActiveAnnouncement(): Promise<Announcement | null> {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const response = await apiFetch(
    `/announcements/active?timeZone=${encodeURIComponent(timeZone)}`,
  );
  return response.announcement || null;
}

export async function dismissAnnouncement(
  id: number,
  action: AnnouncementAction = 'dismissed',
): Promise<void> {
  await apiFetch(`/announcements/${id}/dismiss`, {
    method: 'POST',
    body: JSON.stringify({ action }),
  });
}

export async function listAnnouncements(): Promise<Announcement[]> {
  const response = await apiFetch('/announcements');
  return response.announcements || [];
}

export async function uploadAnnouncementImage(file: File): Promise<AnnouncementImageUpload> {
  if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
    throw new Error('Choose a JPG, PNG, or WebP image');
  }
  if (file.size > MAX_IMAGE_BYTES) {
    throw new Error('Image must be 5 MB or smaller');
  }

  const data = await readAsDataUrl(file);
  return apiFetch('/announcements/image', {
    method: 'POST',
    body: JSON.stringify({
      data,
      contentType: file.type,
      bucket: firebaseConfig.storageBucket,
    }),
  });
}

export async function createAnnouncement(input: AnnouncementInput): Promise<Announcement> {
  const response = await apiFetch('/announcements', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return response.announcement;
}

export async function updateAnnouncement(
  id: number,
  input: Partial<AnnouncementInput>,
): Promise<Announcement> {
  const response = await apiFetch(`/announcements/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
  return response.announcement;
}

export async function setAnnouncementStatus(
  id: number,
  status: AnnouncementStatus,
): Promise<Announcement> {
  const response = await apiFetch(`/announcements/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
  return response.announcement;
}

export async function deleteAnnouncement(id: number): Promise<void> {
  await apiFetch(`/announcements/${id}`, {
    method: 'DELETE',
  });
}
